import React, { useEffect, useLayoutEffect, useRef, useState, useId } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check } from 'lucide-react';

/**
 * Drop-in replacement for a native <select>, styled to match the store: a flat option list with a
 * gold bar on hover and on the selected option. Browsers don't allow styling the native option list,
 * which is why this exists. Use it exactly like <select>:
 *
 *   <Select value={x} onChange={e => setX(e.target.value)} className="...">
 *     <option value="a">A</option>
 *   </Select>
 *
 * onChange receives { target: { value, name } } with the value as a string, like a native select.
 */

function collectOptions(children, out = []) {
  React.Children.forEach(children, child => {
    if (!React.isValidElement(child)) return;
    if (child.type === 'option') {
      const label = React.Children.toArray(child.props.children).join('');
      out.push({ value: String(child.props.value ?? label), label, disabled: !!child.props.disabled });
    } else if (child.props?.children) {
      collectOptions(child.props.children, out); // fragments, optgroups, wrappers
    }
  });
  return out;
}

export default function Select({ value, onChange, children, className = '', disabled = false, name, style, id, ...rest }) {
  const options = collectOptions(children);
  const current = options.find(o => o.value === String(value ?? '')) || options[0];
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const [pos, setPos] = useState(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const typeahead = useRef({ text: '', at: 0 });
  const listId = useId();

  const choose = (opt) => {
    if (!opt || opt.disabled) return;
    setOpen(false);
    triggerRef.current?.focus();
    if (opt.value !== current?.value) onChange?.({ target: { value: opt.value, name }, currentTarget: { value: opt.value, name } });
  };

  const openList = () => {
    if (disabled) return;
    setHighlight(Math.max(0, options.indexOf(current)));
    setOpen(true);
  };

  // Place the panel under the trigger (or above it when there isn't room below)
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const height = Math.min(320, options.length * 38 + 8);
    const below = window.innerHeight - r.bottom;
    const up = below < height + 12 && r.top > below;
    // Grow to fit the longest label; anchor to whichever side of the screen the field is on
    const horizontal = r.left > window.innerWidth / 2 ? { right: window.innerWidth - r.right } : { left: r.left };
    setPos({ ...horizontal, minWidth: Math.max(r.width, 160), width: 'max-content', maxWidth: 360,
             ...(up ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }) });
  }, [open, options.length]);

  // Close on outside click, scroll elsewhere, or resize
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (!panelRef.current?.contains(e.target) && !triggerRef.current?.contains(e.target)) setOpen(false);
    };
    const onScroll = (e) => { if (!panelRef.current?.contains(e.target)) setOpen(false); };
    const onResize = () => setOpen(false);
    document.addEventListener('mousedown', onDown);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open]);

  // Keep the highlighted option visible while using the keyboard
  useEffect(() => {
    if (open && highlight >= 0) panelRef.current?.querySelector(`[data-index="${highlight}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [open, highlight]);

  const step = (from, dir) => {
    for (let i = 1; i <= options.length; i++) {
      const idx = (from + dir * i + options.length) % options.length;
      if (!options[idx].disabled) return idx;
    }
    return from;
  };

  const onKeyDown = (e) => {
    if (disabled) return;
    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) { e.preventDefault(); openList(); }
      return;
    }
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => step(h, 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(h => step(h, -1)); }
    else if (e.key === 'Home') { e.preventDefault(); setHighlight(step(-1, 1)); }
    else if (e.key === 'End') { e.preventDefault(); setHighlight(step(options.length, -1)); }
    else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); choose(options[highlight]); }
    else if (e.key === 'Escape') { e.preventDefault(); setOpen(false); }
    else if (e.key === 'Tab') setOpen(false);
    else if (e.key.length === 1) {
      const now = Date.now();
      const t = typeahead.current;
      t.text = (now - t.at < 600 ? t.text : '') + e.key.toLowerCase();
      t.at = now;
      const idx = options.findIndex(o => !o.disabled && o.label.toLowerCase().startsWith(t.text));
      if (idx >= 0) setHighlight(idx);
    }
  };

  return (
    <>
      <button type="button" ref={triggerRef} id={id} disabled={disabled} onClick={() => (open ? setOpen(false) : openList())} onKeyDown={onKeyDown}
        role="combobox" aria-haspopup="listbox" aria-expanded={open} aria-controls={listId}
        aria-activedescendant={open && highlight >= 0 ? `${listId}-${highlight}` : undefined}
        className={`inline-flex items-center justify-between gap-2 text-left cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
        style={{ ...style, colorScheme: undefined }} {...rest}>
        <span className="truncate">{current?.label ?? ''}</span>
        <ChevronDown className={`w-4 h-4 flex-shrink-0 opacity-50 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && pos && createPortal(
        <div ref={panelRef} id={listId} role="listbox" className="flat-select-panel" style={{ position: 'fixed', ...pos }}>
          {options.map((o, i) => (
            <div key={`${o.value}-${i}`} id={`${listId}-${i}`} data-index={i} role="option" aria-selected={o.value === current?.value}
              aria-disabled={o.disabled || undefined}
              className={`menu-item cursor-pointer ${i === highlight ? 'is-highlighted' : ''} ${o.value === current?.value ? 'is-active' : ''} ${o.disabled ? 'opacity-40 pointer-events-none' : ''}`}
              onMouseEnter={() => setHighlight(i)} onMouseDown={e => e.preventDefault()} onClick={() => choose(o)}>
              <span className="flex-1 truncate">{o.label}</span>
              {o.value === current?.value && <Check className="w-4 h-4" />}
            </div>
          ))}
        </div>,
        document.body,
      )}
    </>
  );
}
